import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { InView } from 'react-intersection-observer'
import clsx from 'clsx'
import { hasTextOverflow } from 'src/components/postCard/postCard.utils'
import { useMentionUtils } from 'src/components/commentInput/hooks'
import i18n from 'src/i18n'
import { NewsPostType, PostType } from 'src/hook/usePosts'
import { UserProfileUtils, useTrackEvents, useUserProfile } from 'src/context/userProfileContext'
import { ComponentName } from 'src/utils/datahubDefaultEvent'
import useSendDataHubConsume from 'src/hook/useSendDatahubConsume/useSendDatahubConsume'
import { Actions, Entities, EventNames, ScreenNames } from 'src/utils/datahub/enums'
import { FeedCardMedia } from '../feedCardMedia'
import { PostDescription, PostTitle, ViewMore } from './feedCardBody.styles'
import { FeedCardBodyProps } from './feedCardBody.types'

const FeedCardBody = ({ post, postPath, isProcessingVideo }: FeedCardBodyProps) => {
  const [viewMoreDescription, setViewMoreDescription] = useState(true)
  const postDescriptionRef = useRef<HTMLDivElement>(null)
  const { convertMentionToHTML } = useMentionUtils()
  const { loggedUserUcode, onOpen: onOpenUserProfile } = useUserProfile()
  const { sendOpenProfileEvent } = useTrackEvents({ loggedUserUcode })
  const { sendDataHubConsume } = useSendDataHubConsume()

  const mediaProps =
    PostType.VIDEO && isProcessingVideo ? { ...post, postType: PostType.TEXT } : post
  const showViewMoreTextAction = viewMoreDescription && hasTextOverflow(postDescriptionRef.current)
  const showPostMediaContainer = [
    PostType.IMAGE,
    PostType.URL,
    PostType.VIDEO,
    NewsPostType.LIVE
  ].includes(post.postType)

  const description = convertMentionToHTML({
    text: post?.description as string,
    mentions: post?.mentions?.map((mention) => ({
      tag: mention?.tag,
      failed: Boolean(mention?.failed),
      failureReason: mention?.failureReason,
      ucode: mention?.metadata?.ucode,
      name: mention?.metadata?.name
    }))
  })

  const sendEventNewFeed = (eventName: EventNames) => {
    sendDataHubConsume({
      action: Actions.VIEW,
      entity: Entities.COMMUNITY,
      event: {
        eventName,
        screenName: ScreenNames.COMMUNITY_NEW_FEED,
        postId: post.id,
        postType: post.postType,
        communityId: Number(post?.ownerCollectionId)
      }
    })
  }

  const handleViewFullPostDescription = (event: React.MouseEvent) => {
    event.preventDefault()
    sendEventNewFeed(EventNames.CLICK_TO_SEE_MORE_POST_DETAIL)
    setViewMoreDescription(!viewMoreDescription)
  }

  const handleOpenPost = () => {
    sendEventNewFeed(EventNames.CLICK_TO_GO_TO_POST_DETAIL)
  }

  useEffect(() => {
    const elementsToOpenProfile = postDescriptionRef.current?.querySelectorAll(
      '[data-action="open-profile"]'
    )

    const callback = ({ customEvent, ucode }) => {
      onOpenUserProfile(customEvent, ucode)
      sendOpenProfileEvent({
        componentName: ComponentName.POST_FEED,
        userUcode: ucode,
        accessFromMention: true
      })
    }

    const removeListener = UserProfileUtils.openEventListener({
      elements: elementsToOpenProfile,
      componentName: ComponentName.POST_FEED,
      callback
    })

    return removeListener
  }, [])

  return (
    <>
      <InView
        as="div"
        onChange={
          /* istanbul ignore next */ (inView) =>
            inView && sendEventNewFeed(EventNames.VIEW_POST_PREVIEW)
        }
      />

      <div className="_pl-4 _pr-4 _pb-4">
        <div
          className={clsx({
            '_mb-4': showPostMediaContainer
          })}
        >
          <Link
            to={postPath}
            title={post.title}
            className="_text-decoration-none _d-block"
            onClick={handleOpenPost}
          >
            <PostTitle
              title={post.title}
              className="_text-2 _text-gray-900 _font-weight-extrabold _mb-1"
            >
              {post.title}
            </PostTitle>

            {post?.description && (
              <PostDescription
                ref={postDescriptionRef}
                className="_font-14 _text-gray-900 _mb-1"
                limitChars={viewMoreDescription}
                dangerouslySetInnerHTML={{ __html: description }}
              />
            )}
          </Link>

          {showViewMoreTextAction && (
            <ViewMore
              href="#"
              className="_font-14 _text-blue-light"
              title={i18n.t('feed.view_more')}
              onClick={handleViewFullPostDescription}
            >
              {i18n.t('feed.view_more')}
            </ViewMore>
          )}
        </div>

        {showPostMediaContainer && <FeedCardMedia post={mediaProps} />}
      </div>
    </>
  )
}

export default FeedCardBody
